import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import actions from 'redux/actions'
import { DragLayer } from 'sb-react-dnd'
import { constants, mixpanel, snowplow, compose } from 'common/helpers'

import injectGlobals from 'decorators/injectGlobals'

import AdaMessages from './AdaMessages/AdaMessages'
import Items from './Items/Items'

import s from './Queue.scss'


const defaultPlacement  = 'queue'
const ariaDescribedby   = 'productDescription'


class Queue extends Component {

  static propTypes = {
    items: PropTypes.array,
    viewOnly: PropTypes.bool,
    placement: PropTypes.string,
    className: PropTypes.string,
    trackingNumberPlacement: PropTypes.string,
    isTutorialEnabled: PropTypes.bool, // @injectGlobals
    isEmptyQueueClickEnabled: PropTypes.bool, // @injectGlobals
    isMobile: PropTypes.bool, // @injectGlobals
    emptyQueueFirstCellPlus: PropTypes.bool,
    humbleMobileProduct: PropTypes.bool,
    square: PropTypes.bool,
    startIndex: PropTypes.number,
  }

  static defaultProps = {
    startIndex: 0,
  }

  blurTimeout = null

  state = {
    totalCount: null,
    lockedCount: null,
    selectedProduct: {},
    isProductSelected: false,
    isProductMoved: false,
    isProductDropped: false,
    isProductDeleted: false,
    isProductCanceled: false,
  }

  handleItemMove = (oldIndex, newIndex) => {
    const { placement, items, startIndex } = this.props
    const products = items.reduce((products, item) => [ ...products, ...item.products ], [])
    const product = products[oldIndex]
    const metadata = {
      placement,
      id: product.id,
      name: product.name,
      brand: product.brand,
    }

    return actions.models.queue.moveItem({ oldIndex: oldIndex + startIndex, newIndex: newIndex + startIndex, metadata })
  }

  handleItemRemove = ({ event, id, index, name, brand }) => {
    const { placement } = this.props

    event.preventDefault()
    event.stopPropagation()

    return actions.models.queue.removeItem({
      id,
      index,
      page: placement,
      metadata: {
        placement: defaultPlacement,
        name,
        brand,
      },
    })
  }

  handleItemClick = (withTutorial) => ({ id } = {}) => {
    const { isTutorialEnabled, isEmptyQueueClickEnabled } = this.props

    // !id when there is no product - check QueueItem onClick() method w/o params
    if (!id && withTutorial) {
      snowplow.track(constants.snowplowEvents.emptyQueueClick)

      if (isEmptyQueueClickEnabled) {
        actions.models.modals.open(constants.modals.EmptyQueueRecommendations)
      }
      else if (isTutorialEnabled) {
        actions.models.modals.open(constants.modals.QueueTutorial)
      }
    }
    else if (id) {
      mixpanel.track(constants.analyticsEvents.quickViewButton, {
        placement: 'queue',
      })

      actions.models.modals.open(constants.modals.ProductDetails, { productId: id })
    }
  }

  onTrackingNumberClick = () => {
    const { trackingNumberPlacement } = this.props

    mixpanel.track(constants.analyticsEvents.trackingNumberClick, {
      placement: trackingNumberPlacement,
    })
  }

  updateSelectedProductState = (newSelectedProductState) => {
    const { selectedProduct } = this.state

    this.setState({
      selectedProduct,
      isProductMoved: false,
      isProductDeleted: false,
      isProductDropped: false,
      isProductSelected: false,
      isProductCanceled: false,
      ...newSelectedProductState,
    })
  }

  setSelectedProduct = ({ id, index, name, brand }) => {
    const { items } = this.props

    const productTitle  = `${name} by ${brand}`
    const queueItemData = items.find(({ products }) => products.some((product) => product.id === id))
    const itemsStatuses = items.reduce((statuses, { status }) => statuses.concat(status), [])
    const totalCount    = itemsStatuses.filter((itemStatus) => itemStatus === 'product').length
    const lockedCount   = itemsStatuses.filter((itemStatus) => itemStatus === 'locked').length

    const { month, year } = queueItemData

    this.setState({
      totalCount,
      lockedCount,
      selectedProduct: { id, index, month, year, title: productTitle },
      isProductSelected: true,
    })
  }

  handleItemBlur = () => {
    this.blurTimeout = setTimeout(() => {
      this.setState({
        totalCount: null,
        lockedCount: null,
        selectedProduct: {},
        isProductSelected: false,
        isProductMoved: false,
        isProductDropped: false,
        isProductDeleted: false,
        isProductCanceled: false,
      })
    }, 100)
  }

  handleItemFocus = ({ id, index, name, brand }) => {
    const { isProductMoved } = this.state

    if (isProductMoved) {
      clearTimeout(this.blurTimeout)
      this.setSelectedProduct({ id, index, name, brand })
    }
  }

  handleItemKeyDown = ({ event, id, index, name, brand }) => {
    const { isProductSelected, isProductMoved, totalCount, lockedCount } = this.state

    const isSpacebar = event.keyCode === 32
    const queueIndex = index + lockedCount

    if (event.key === 'Backspace' || event.key === 'Delete') {
      this.handleItemRemove({ event, id, index, name, brand })
        .then(() => {
          this.updateSelectedProductState({
            isProductDeleted: true,
          })
        })
    }

    if (!isProductSelected) {
      if (isSpacebar && index >= 0) {
        event.preventDefault()

        this.setSelectedProduct({ id, index, name, brand })
      }
      if (event.key === 'Enter') {
        this.handleItemClick(false)({ id })
      }
    }
    else {
      if (event.key === 'Escape' || event.key === 'Tab' || isSpacebar) {
        if (isSpacebar) {
          event.preventDefault()
        }

        if (isProductMoved) {
          this.setState({
            isProductSelected: false,
            isProductDropped: true,
          })
        }
        else {
          this.updateSelectedProductState({
            isProductSelected: false,
            isProductCanceled: true,
          })
        }
      }
      else if (event.key === 'ArrowLeft' && index) {
        this.handleItemMove(queueIndex, queueIndex - 1)
          .then(() => {
            this.updateSelectedProductState({
              isProductMoved: true,
            })

            document.querySelectorAll(`[aria-describedby="${ariaDescribedby}"`)[queueIndex - 1].focus()
          })
      }
      else if (event.key === 'ArrowRight' && totalCount > index + 1) {
        this.handleItemMove(queueIndex, queueIndex + 1)
          .then(() => {
            this.updateSelectedProductState({
              isProductMoved: true,
            })

            document.querySelectorAll(`[aria-describedby="${ariaDescribedby}"`)[queueIndex + 1].focus()
          })
      }
    }
  }

  render() {
    const { items, viewOnly, isMobile, className, square, emptyQueueFirstCellPlus } = this.props

    if (!items) {
      return null
    }

    const rootClassName = cx(s.queue, className)

    if (viewOnly) {
      return (
        <div className={rootClassName} id="queue">
          <Items
            items={items}
            viewOnly={viewOnly}
            square={square}
            onClick={this.handleItemClick(false)}
          />
        </div>
      )
    }

    const sortableProps = {
      square,
      ariaDescribedby,
      emptyQueueFirstCellPlus,
      onBlur: this.handleItemBlur,
      onFocus: this.handleItemFocus,
      onClick: this.handleItemClick(true),
      onRemove: this.handleItemRemove,
      onKeyDown: this.handleItemKeyDown,
      onTrackingNumberClick: this.onTrackingNumberClick,
      // Disable ft when emptyQueueFirstCellPlus is provided
      isQueueEmptyPlusCellEnabled: typeof emptyQueueFirstCellPlus !== 'boolean',
    }

    return (
      <Fragment>
        <AdaMessages {...this.state} />
        <DragLayer onDragEnd={this.handleItemMove} isMobile={isMobile}>
          <div className={rootClassName} id="queue">
            <Items items={items} {...sortableProps} />
          </div>
        </DragLayer>
      </Fragment>
    )
  }
}


export default compose(
  injectGlobals(({ globals, getFeatureToggle }) => ({
    isTutorialEnabled: getFeatureToggle(constants.features.tutorial),
    isEmptyQueueClickEnabled: getFeatureToggle(constants.features.emptyQueueClick),
    isMobile: globals.device.isMobile,
  })),
)(Queue)
